import {
  ComponentProps,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'

type Theme = 'light' | 'dark'

const ThemeContext = createContext<{
  theme: Theme
  setTheme: Dispatch<SetStateAction<Theme>>
  toggleTheme: () => void
} | null>(null)

const getInitialTheme = (): Theme => {
  const storedTheme = localStorage.getItem('theme') as Theme | null

  if (storedTheme) {
    return storedTheme
  }

  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  return prefersDark ? 'dark' : 'light'
}

export const ThemeProvider = (props: ComponentProps<'div'>) => {
  const [theme, set] = useState<Theme>(getInitialTheme())

  const setTheme: Dispatch<SetStateAction<Theme>> = (value) => {
    const nextTheme = typeof value === 'function' ? value(theme) : value

    set(nextTheme)
    localStorage.setItem('theme', nextTheme)
  }

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, toggleTheme }}
      {...props}
    />
  )
}

export const useTheme = () => {
  const context = useContext(ThemeContext)

  if (!context) throw new Error('useTheme must be used within ThemeProvider')

  return context
}
